import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import { FC } from 'react';
import { useDispatch } from 'react-redux';
import {
  addAccountId,
  addQueryFilter,
  ClientIntegrationPlatformConfig,
  QueryFilter,
  removeQueryFilter,
  updateAccountId,
  updateQueryFilter
} from './platform-config/platform-config.slice';

interface AccountConfigurationsProps {
  platformType: string;
  accountTypeDisplayName: string;
  accountIds: string[];
  platformConfigs: ClientIntegrationPlatformConfig[];
  validateLoading: boolean;
  integrationLoading: boolean;
  submitError: string | null;
  submitSuccess: string | null;
  onRemoveAccountId: (accountId: string) => void;
}

const AccountConfigurations: FC<AccountConfigurationsProps> = ({
  platformType,
  accountTypeDisplayName,
  accountIds,
  platformConfigs,
  validateLoading,
  integrationLoading,
  submitError,
  submitSuccess,
  onRemoveAccountId
}) => {
  const dispatch = useDispatch();
  const handleAccountIdChange = (
    originalAccountId: string,
    newAccountId: string
  ) => {
    dispatch(
      updateAccountId({ platformType, originalAccountId, newAccountId })
    );
  };

  const handleRemoveAccountId = (accountId: string) => {
    onRemoveAccountId(accountId);
  };

  const updateFilterField = (
    accountId: string,
    filterId: number,
    field: keyof QueryFilter,
    value: any
  ) => {
    const existingConfig = platformConfigs.find(
      (config) => config.configIdentifier === accountId
    );
    const existingFilter = existingConfig?.queryFilters.find(
      (filter) => filter.id === filterId
    );

    if (!existingFilter) return;

    const updatedFilter: QueryFilter = {
      ...existingFilter,
      [field]: value
    };

    dispatch(
      updateQueryFilter({
        platformType,
        accountId,
        updatedQueryFilter: updatedFilter
      })
    );
  };

  const handleRemoveFilter = (accountId: string, filterId: number) => {
    dispatch(removeQueryFilter({ platformType, accountId, filterId }));
  };

  const handleAddFilter = (accountId: string, filterId: number) => {
    dispatch(
      addQueryFilter({
        platformType,
        accountId,
        queryFilter: {
          id: filterId,
          field: 'Campaign Name',
          operator: 'contains',
          value: ''
        }
      })
    );
  };

  const handleAddAccountId = () => {
    const newAccountId = '';

    dispatch(
      addAccountId({
        platformType,
        newAccountId
      })
    );
  };

  return (
    <>
      {integrationLoading && (
        <div className="flex items-center justify-center text-sm text-primary-500 w-full mb-4">
          <i className="text-sm mr-2" />
          <p>Getting {accountTypeDisplayName} Configuration...</p>
        </div>
      )}

      {validateLoading && (
        <div className="flex items-center justify-center text-sm text-primary-500 w-full">
          <i className="text-sm mr-2" />
          <p>Validating and completing setup...</p>
        </div>
      )}

      {submitSuccess && (
        <div className="flex items-center justify-center text-sm text-tertiary-600 w-full">
          <i className="ra-icons-generic-check text-sm mr-2" />
          <p className="text-sm mb-1 text-tertiary-600 capitalize">
            {submitSuccess}
          </p>
        </div>
      )}

      {submitError && (
        <div className="flex items-center justify-center text-sm text-error-300 w-full">
          <i className="ra-icons-generic-cross text-sm mr-2" />
          <span className="mb-1">{submitError}</span>
        </div>
      )}

      {!integrationLoading && (
        <>
          {accountIds.map((accountId, index) => {
            const currentFilters = platformConfigs[index]?.queryFilters ?? [];

            return (
              <div
                key={index}
                className="w-full border border-neutral-200 rounded-md px-4 pt-4 pb-6 mt-4 mb-2 relative"
              >
                <div className="absolute -top-3 left-4 bg-white px-2 text-sm font-medium text-secondary-400">
                  {accountTypeDisplayName} Configuration {index + 1}
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="text"
                    value={accountId}
                    onChange={(e) =>
                      handleAccountIdChange(accountId, e.target.value)
                    }
                    className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:border-secondary-400 transition-all outline-none text-sm"
                    placeholder={`Enter ${accountTypeDisplayName} ID #${
                      index + 1
                    }`}
                  />
                  {accountIds.length > 1 && (
                    <Button
                      identifier={`delete-accountId-${index}`}
                      theme={ButtonTheme.CANCEL}
                      onClick={() => handleRemoveAccountId(accountId)}
                    >
                      <i className="flex items-center ra-icons-generic-bin text-lg"></i>
                    </Button>
                  )}
                </div>

                {currentFilters.map((filter, filterIndex) => (
                  <div key={filter.id} className="flex flex-wrap gap-2 mb-2">
                    <select
                      value={filter.field}
                      onChange={(e) =>
                        updateFilterField(
                          accountId,
                          filter.id,
                          'field',
                          e.target.value
                        )
                      }
                      className="flex-1 min-w-[150px] px-3 py-2 border border-neutral-200 rounded-md text-sm text-primary-700 focus:border-secondary-400 outline-none transition-all h-[40px]"
                    >
                      <option value="Campaign Name">Campaign Name</option>
                    </select>

                    <select
                      value={filter.operator}
                      onChange={(e) =>
                        updateFilterField(
                          accountId,
                          filter.id,
                          'operator',
                          e.target.value
                        )
                      }
                      className="flex-1 min-w-[100px] px-3 py-2 border border-neutral-200 rounded-md text-sm text-primary-700 focus:border-secondary-400 outline-none transition-all uppercase h-[40px]"
                    >
                      <option value="contains">CONTAINS</option>
                    </select>

                    <input
                      type="text"
                      value={filter.value}
                      onChange={(e) =>
                        updateFilterField(
                          accountId,
                          filter.id,
                          'value',
                          e.target.value
                        )
                      }
                      placeholder="Enter filter value"
                      className="flex-1 min-w-[150px] px-3 py-2 border border-neutral-200 rounded-md text-sm text-primary-700 focus:border-secondary-400 outline-none transition-all h-[40px]"
                    />

                    <Button
                      identifier={`remove-filter-${filterIndex}`}
                      theme={ButtonTheme.CANCEL}
                      onClick={() => handleRemoveFilter(accountId, filter.id)}
                    >
                      <i className="ra-icons-generic-bin text-lg flex items-center"></i>
                    </Button>
                  </div>
                ))}
                {currentFilters.length === 0 && (
                  <div className="flex justify-center mt-2">
                    <button
                      type="button"
                      onClick={() => handleAddFilter(accountId, index)}
                      className="text-primary-500 hover:text-primary-700 flex items-center text-sm mt-3"
                    >
                      <i className="ra-icons-generic-plus mr-1 mt-1 text-xs" />
                      Add Filter
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={handleAddAccountId}
            className="text-primary-500 hover:text-primary-700 flex items-center text-sm"
          >
            <i className="ra-icons-generic-plus mr-1 text-xs mt-1" />
            Add {accountTypeDisplayName} Configuration
          </button>
        </>
      )}
    </>
  );
};

export default AccountConfigurations;
